import React, { useMemo, useState, useEffect } from "react";
import { Box, Button, Divider, Grid, Paper, IconButton } from "@material-ui/core";
import { grey, orange } from "@material-ui/core/colors";
import { ChevronLeft, DeleteOutline, Warning, Clear } from "@material-ui/icons";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CTextField, CSelect, MultiSelect, Text, HFlex, ProgressButton } from "shared";
import styled from "styled-components";
import { ReferralItem, ExtractedFields, FieldName, computeValidationIssues } from "./types";
import { PdfViewer } from "./PdfViewer";
import { siteMapping } from "./extraction";

const OTHER_PHONE_VALUE = "__other__";

const Navigator = styled(Box)`
  padding: 12px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${grey[100]};
  border-radius: 8px;
  margin-bottom: 16px;
`;

const SplitView = styled(Grid)`
  & > .MuiGrid-item {
    display: flex;
    & > * {
      flex: 1;
    }
  }
`;

const FormPanel = styled(Paper)`
  padding: 24px;
  min-height: 780px;
`;

const PdfPanel = styled(Box)`
  min-height: 780px;
`;

const WarningBanner = styled(HFlex)`
  background: ${orange[50]};
  border: 1px solid ${orange[300]};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  align-items: flex-start;
  gap: 12px;
`;

interface Props {
  items: ReferralItem[];
  currentIndex: number;
  onUpdateFields: (id: string, fields: Partial<ExtractedFields>) => void;
  onRemove: (id: string) => void;
  onNext: () => void;
  onPrev: () => void;
  onSkipToSummary: () => void;
}

const urgencyOptions = [
  { value: "Routine", label: "Routine" },
  { value: "Urgent", label: "Urgent" },
  { value: "2-week wait", label: "2-week wait" },
];

const siteOptions = [...new Set(Object.values(siteMapping))].map(site => ({
  value: site,
  label: site,
}));

const bodyPartOptions = [
  { value: "left_knee", label: "Left Knee" },
  { value: "right_knee", label: "Right Knee" },
  { value: "left_hip", label: "Left Hip" },
  { value: "right_hip", label: "Right Hip" },
  { value: "left_shoulder", label: "Left Shoulder" },
  { value: "right_shoulder", label: "Right Shoulder" },
  { value: "lower_back", label: "Lower Back" },
  { value: "neck", label: "Neck" },
  { value: "left_hand", label: "Left Hand" },
  { value: "right_hand", label: "Right Hand" },
  { value: "left_wrist", label: "Left Wrist" },
  { value: "right_wrist", label: "Right Wrist" },
  { value: "left_foot", label: "Left Foot" },
  { value: "right_foot", label: "Right Foot" },
  { value: "left_ankle", label: "Left Ankle" },
  { value: "right_ankle", label: "Right Ankle" },
  { value: "left_elbow", label: "Left Elbow" },
  { value: "right_elbow", label: "Right Elbow" },
  { value: "left_lower_leg", label: "Left Lower Leg" },
  { value: "right_lower_leg", label: "Right Lower Leg" },
];

const bilateralJointOptions = [
  { value: "bilateral_knee", label: "Bilateral Knee" },
  { value: "bilateral_hip", label: "Bilateral Hip" },
  { value: "bilateral_shoulder", label: "Bilateral Shoulder" },
  { value: "bilateral_ankle", label: "Bilateral Ankle" },
  { value: "bilateral_hand", label: "Bilateral Hand" },
  { value: "bilateral_wrist", label: "Bilateral Wrist" },
  { value: "bilateral_foot", label: "Bilateral Foot" },
  { value: "bilateral_elbow", label: "Bilateral Elbow" },
  { value: "bilateral_lower_leg", label: "Bilateral Lower Leg" },
];

const getInvitedJointOptions = (selectedBodyParts?: string[]) => {
  const allOptions = [...bodyPartOptions, ...bilateralJointOptions];
  if (!selectedBodyParts || selectedBodyParts.length === 0) {
    return allOptions;
  }
  return allOptions.filter((opt) => {
    if (selectedBodyParts.includes(opt.value)) return true;
    if (opt.value.startsWith("bilateral_")) {
      const joint = opt.value.replace("bilateral_", "");
      return selectedBodyParts.includes(`left_${joint}`) && selectedBodyParts.includes(`right_${joint}`);
    }
    return false;
  });
};

export const BatchReviewStep: React.VFC<Props> = ({
  items,
  currentIndex,
  onUpdateFields,
  onRemove,
  onNext,
  onPrev,
  onSkipToSummary,
}) => {
  const { t } = useTranslation();

  const currentItem = items[currentIndex];
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === items.length - 1;

  const form = useForm<ExtractedFields>({
    defaultValues: currentItem.extracted || {},
  });

  const watchedValues = form.watch();

  const invitedJointOptions = useMemo(
    () => getInvitedJointOptions(watchedValues.body_parts),
    [watchedValues.body_parts]
  );

  const [useCustomPhone, setUseCustomPhone] = useState(false);

  const phoneOptions = useMemo(() => {
    const phones = watchedValues.phone_numbers || [];
    if (phones.length === 0) return [];
    const options = phones.map(p => ({ value: p, label: p }));
    options.push({ value: OTHER_PHONE_VALUE, label: t("form.other_phone_number") });
    return options;
  }, [watchedValues.phone_numbers, t]);

  const hasExtractedPhones = phoneOptions.length > 0;

  // When "Other" is selected in dropdown, switch to text input and clear value
  useEffect(() => {
    if (watchedValues.phone === OTHER_PHONE_VALUE) {
      setUseCustomPhone(true);
      form.setValue("phone", "");
    }
  }, [watchedValues.phone, form]);

  React.useEffect(() => {
    if (currentItem.extracted) {
      form.reset(currentItem.extracted);
      // Check if phone is custom (not in extracted list)
      const phone = currentItem.extracted.phone;
      const phones = currentItem.extracted.phone_numbers || [];
      const isCustom = !!(phone && phones.length > 0 && !phones.includes(phone));
      setUseCustomPhone(isCustom);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem.id]);

  React.useEffect(() => {
    onUpdateFields(currentItem.id, watchedValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(watchedValues)]);

  const liveIssues = useMemo(() => computeValidationIssues(watchedValues), [watchedValues]);

  const issueFields = new Set(liveIssues.map(i => i.field));

  const hasIssue = (field: FieldName | "contact"): boolean => {
    if (field === "contact") {
      return issueFields.has("contact") || issueFields.has("email") || issueFields.has("phone");
    }
    return issueFields.has(field);
  };

  return (
    <Box>
      <Navigator>
        <HFlex gap={2}>
          {!isFirst && (
            <Button onClick={onPrev} startIcon={<ChevronLeft />}>
              {t("buttons.previous")}
            </Button>
          )}
          <IconButton
            size="small"
            onClick={() => onRemove(currentItem.id)}
            title={t("buttons.remove")}
          >
            <DeleteOutline />
          </IconButton>
        </HFlex>
        <Text secondary>
          {currentIndex + 1} / {items.length}
        </Text>
        <HFlex gap={2}>
          {!isLast && (
            <Button onClick={onSkipToSummary}>
              {t("buttons.skip_to_summary")}
            </Button>
          )}
          <ProgressButton loading={false} onClick={onNext}>
            {isLast ? t("buttons.finish_review") : t("buttons.next")}
          </ProgressButton>
        </HFlex>
      </Navigator>

      <SplitView container spacing={3}>
        <Grid item xs={6}>
          <FormPanel elevation={2}>
            {liveIssues.length > 0 && (
              <WarningBanner>
                <Warning style={{ color: orange[700], fontSize: 28, flexShrink: 0 }} />
                <Box>
                  <Text bold style={{ color: orange[900] }}>{t("form.batch_validation_issues")}</Text>
                  <Box component="ul" style={{ margin: "4px 0 0", paddingLeft: 16, fontSize: 13, color: orange[800] }}>
                    {liveIssues.map((issue, idx) => (
                      <li key={idx}>{issue.message}</li>
                    ))}
                  </Box>
                </Box>
              </WarningBanner>
            )}
            <FormProvider {...form}>
              <form>
                <Box mb={2}>
                  <Text bold>{t("form.patient_details")}</Text>
                  <CTextField
                    name="patient_name"
                    label={t("form.patient_name")}
                    margin="normal"
                    size="small"
                    fullWidth
                    error={hasIssue("patient_name")}
                  />
                  <HFlex gap={2}>
                    <CTextField
                      name="date_of_birth"
                      label={t("form.date_of_birth")}
                      type="date"
                      margin="normal"
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                    <CTextField
                      name="nhs_number"
                      label={t("form.nhs_number")}
                      margin="normal"
                      size="small"
                      fullWidth
                      error={hasIssue("nhs_number")}
                    />
                  </HFlex>
                  <HFlex gap={2}>
                    <CTextField
                      name="email"
                      label={t("form.email")}
                      type="email"
                      margin="normal"
                      size="small"
                      fullWidth
                      error={hasIssue("contact")}
                    />
                    {hasExtractedPhones && !useCustomPhone ? (
                      <CSelect
                        name="phone"
                        label={t("form.phone")}
                        items={phoneOptions}
                        margin="normal"
                        size="small"
                        fullWidth
                      >
                        {({ label }) => <Text>{label}</Text>}
                      </CSelect>
                    ) : (
                      <CTextField
                        name="phone"
                        label={t("form.phone")}
                        margin="normal"
                        size="small"
                        fullWidth
                        error={hasIssue("contact")}
                        InputProps={hasExtractedPhones ? {
                          endAdornment: (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setUseCustomPhone(false);
                                form.setValue("phone", "");
                              }}
                            >
                              <Clear fontSize="small" />
                            </IconButton>
                          ),
                        } : undefined}
                      />
                    )}
                  </HFlex>
                  <CTextField
                    name="address"
                    label={t("form.address")}
                    margin="normal"
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Box>

                <Divider />

                <Box my={2}>
                  <Text bold>{t("form.referral_details")}</Text>
                  <HFlex gap={2}>
                    <CSelect
                      name="site"
                      label={t("form.site")}
                      items={siteOptions}
                      margin="normal"
                      size="small"
                      fullWidth
                    >
                      {({ label }) => <Text>{label}</Text>}
                    </CSelect>
                    <CTextField
                      name="referral_date"
                      label={t("form.referral_date")}
                      type="date"
                      margin="normal"
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </HFlex>
                  <CSelect
                    name="urgency"
                    label={t("form.urgency")}
                    items={urgencyOptions}
                    margin="normal"
                    size="small"
                    fullWidth
                  >
                    {({ label }) => <Text>{label}</Text>}
                  </CSelect>
                  <CTextField
                    name="pathway_ids"
                    label={t("form.pathway_ids")}
                    margin="normal"
                    size="small"
                    fullWidth
                    error={hasIssue("pathway_ids")}
                  />
                  <MultiSelect
                    name="body_parts"
                    label={t("form.body_parts")}
                    options={bodyPartOptions}
                    getOptionLabel={opt => opt.label}
                    getOptionValue={opt => opt.value}
                    margin="normal"
                    disableCloseOnSelect
                    error={hasIssue("body_parts")}
                  />
                  <CSelect
                    name="invited_joint"
                    label={t("form.batch_invited_joint")}
                    items={invitedJointOptions}
                    margin="normal"
                    size="small"
                    fullWidth
                  >
                    {({ label }) => <Text>{label}</Text>}
                  </CSelect>
                </Box>

                <Divider />

                <Box my={2}>
                  <Text bold>{t("form.gp_details_optional")}</Text>
                  <CTextField
                    name="gp_name"
                    label={t("form.gp_name")}
                    margin="normal"
                    size="small"
                    fullWidth
                  />
                  <CTextField
                    name="gp_address"
                    label={t("form.gp_address")}
                    margin="normal"
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Box>
              </form>
            </FormProvider>
          </FormPanel>
        </Grid>
        <Grid item xs={6}>
          <PdfPanel>
            <PdfViewer file={currentItem.file} />
          </PdfPanel>
        </Grid>
      </SplitView>
    </Box>
  );
};
